<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <router-link to="/super-admin/delivery" class="gl-back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Dashboard
        </router-link>
        <h1 class="gl-page-title">Delivery Settings</h1>
        <p class="gl-page-sub">Configure providers, fees, commissions and bonuses</p>
      </div>
      <button class="gl-btn-primary" @click="saveSettings" :disabled="saving">
        <span v-if="saving" class="gl-spin"></span>
        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        Save All Settings
      </button>
    </div>

    <div class="gl-ds-layout">
      <!-- Left column -->
      <div class="gl-ds-col">

        <!-- Providers -->
        <div class="gl-ds-card">
          <div class="gl-ds-card-title">Delivery Providers</div>
          <div class="gl-ds-provider-list">
            <div class="gl-ds-provider-row">
              <div class="gl-ds-provider-info">
                <div class="gl-ds-provider-icon gl-icon-inhouse">🏠</div>
                <div>
                  <div class="gl-ds-provider-name">In-House Delivery</div>
                  <div class="gl-ds-provider-desc">Use your own riders for deliveries</div>
                </div>
              </div>
              <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': settings.inhouse_enabled }" @click="settings.inhouse_enabled = !settings.inhouse_enabled">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>
            <div class="gl-ds-provider-row">
              <div class="gl-ds-provider-info">
                <div class="gl-ds-provider-icon gl-icon-lalamove">🚗</div>
                <div>
                  <div class="gl-ds-provider-name">Lalamove</div>
                  <div class="gl-ds-provider-desc">Third-party delivery service</div>
                </div>
              </div>
              <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': settings.lalamove_enabled }" @click="settings.lalamove_enabled = !settings.lalamove_enabled">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>
            <div class="gl-ds-provider-row">
              <div class="gl-ds-provider-info">
                <div class="gl-ds-provider-icon gl-icon-grab">🟢</div>
                <div>
                  <div class="gl-ds-provider-name">Grab Express</div>
                  <div class="gl-ds-provider-desc">Third-party delivery service</div>
                </div>
              </div>
              <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': settings.grab_enabled }" @click="settings.grab_enabled = !settings.grab_enabled">
                <span class="gl-toggle-thumb"></span>
              </button>
            </div>
          </div>
        </div>

        <!-- Auto-Assignment -->
        <div class="gl-ds-card">
          <div class="gl-ds-card-title">Auto-Assignment</div>
          <div class="gl-ds-toggle-row" style="margin-bottom:16px">
            <div>
              <div class="gl-ds-field-label">Enable Auto-Assignment</div>
              <div class="gl-ds-field-hint">Automatically assign riders to orders</div>
            </div>
            <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': settings.auto_assign_enabled }" @click="settings.auto_assign_enabled = !settings.auto_assign_enabled">
              <span class="gl-toggle-thumb"></span>
            </button>
          </div>
          <div v-if="settings.auto_assign_enabled" class="gl-ds-row-2">
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Assignment Radius <span class="gl-ds-unit">(km)</span></label>
              <input type="number" class="gl-ds-input" v-model.number="settings.auto_assign_radius_km" min="1" max="50">
            </div>
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Timeout <span class="gl-ds-unit">(minutes)</span></label>
              <input type="number" class="gl-ds-input" v-model.number="settings.assignment_timeout_minutes" min="1" max="30">
            </div>
          </div>
        </div>

        <!-- Rider Commission -->
        <div class="gl-ds-card">
          <div class="gl-ds-card-title">Rider Commission</div>
          <div class="gl-ds-field" style="margin-bottom:16px">
            <label class="gl-ds-field-label">Commission Type</label>
            <select class="gl-ds-input" v-model="settings.commission_type">
              <option v-for="(label, value) in meta.commission_types" :key="value" :value="value">{{ label }}</option>
            </select>
          </div>
          <div v-if="settings.commission_type === 'fixed'" class="gl-ds-field">
            <label class="gl-ds-field-label">Fixed Amount <span class="gl-ds-unit">(RM per delivery)</span></label>
            <div class="gl-ds-input-prefix-wrap">
              <span class="gl-ds-prefix">RM</span>
              <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.commission_fixed_amount" step="0.01" min="0">
            </div>
          </div>
          <div v-if="settings.commission_type === 'percentage'" class="gl-ds-field">
            <label class="gl-ds-field-label">Percentage <span class="gl-ds-unit">(% of delivery fee)</span></label>
            <div class="gl-ds-input-suffix-wrap">
              <input type="number" class="gl-ds-input gl-ds-input-has-suffix" v-model.number="settings.commission_percentage" step="1" min="0" max="100">
              <span class="gl-ds-suffix">%</span>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column -->
      <div class="gl-ds-col">

        <!-- Fee Calculation -->
        <div class="gl-ds-card">
          <div class="gl-ds-card-title">Fee Calculation</div>
          <div class="gl-ds-field" style="margin-bottom:16px">
            <label class="gl-ds-field-label">Calculation Method</label>
            <select class="gl-ds-input" v-model="settings.fee_calculation_method">
              <option v-for="(label, value) in meta.fee_calculation_methods" :key="value" :value="value">{{ label }}</option>
            </select>
          </div>

          <!-- Fixed -->
          <div v-if="settings.fee_calculation_method === 'fixed'" class="gl-ds-field" style="margin-bottom:16px">
            <label class="gl-ds-field-label">Fixed Delivery Fee</label>
            <div class="gl-ds-input-prefix-wrap">
              <span class="gl-ds-prefix">RM</span>
              <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.fixed_delivery_fee" step="0.01" min="0">
            </div>
          </div>

          <!-- Distance -->
          <div v-if="settings.fee_calculation_method === 'distance'">
            <div class="gl-ds-row-2" style="margin-bottom:12px">
              <div class="gl-ds-field">
                <label class="gl-ds-field-label">Base Fee <span class="gl-ds-unit">(RM)</span></label>
                <div class="gl-ds-input-prefix-wrap">
                  <span class="gl-ds-prefix">RM</span>
                  <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.base_fee" step="0.01" min="0">
                </div>
              </div>
              <div class="gl-ds-field">
                <label class="gl-ds-field-label">Base Distance <span class="gl-ds-unit">(km)</span></label>
                <input type="number" class="gl-ds-input" v-model.number="settings.base_distance_km" step="0.1" min="0">
              </div>
            </div>
            <div class="gl-ds-row-2" style="margin-bottom:16px">
              <div class="gl-ds-field">
                <label class="gl-ds-field-label">Per KM Fee <span class="gl-ds-unit">(RM)</span></label>
                <div class="gl-ds-input-prefix-wrap">
                  <span class="gl-ds-prefix">RM</span>
                  <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.per_km_fee" step="0.01" min="0">
                </div>
              </div>
              <div class="gl-ds-field">
                <label class="gl-ds-field-label">Max Fee <span class="gl-ds-unit">(RM, optional)</span></label>
                <div class="gl-ds-input-prefix-wrap">
                  <span class="gl-ds-prefix">RM</span>
                  <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.max_delivery_fee" step="0.01" min="0">
                </div>
              </div>
            </div>
          </div>

          <!-- Zone -->
          <div v-if="settings.fee_calculation_method === 'zone'" style="margin-bottom:16px">
            <router-link to="/super-admin/delivery/zones" class="gl-btn-outline-inline">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
              Manage Delivery Zones
            </router-link>
          </div>

          <div class="gl-ds-divider"></div>

          <div class="gl-ds-row-2">
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Max Delivery Distance <span class="gl-ds-unit">(km)</span></label>
              <input type="number" class="gl-ds-input" v-model.number="settings.max_delivery_distance_km" step="0.1" min="1">
            </div>
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Free Delivery Min Order <span class="gl-ds-unit">(RM, optional)</span></label>
              <div class="gl-ds-input-prefix-wrap">
                <span class="gl-ds-prefix">RM</span>
                <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.min_order_for_free_delivery" step="0.01" min="0">
              </div>
            </div>
          </div>
        </div>

        <!-- Rider Bonuses -->
        <div class="gl-ds-card">
          <div class="gl-ds-card-title">Rider Bonuses</div>
          <div class="gl-ds-row-3" style="margin-bottom:20px">
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Peak Hour <span class="gl-ds-unit">(RM)</span></label>
              <div class="gl-ds-input-prefix-wrap">
                <span class="gl-ds-prefix">RM</span>
                <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.peak_hour_bonus" step="0.01" min="0">
              </div>
            </div>
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Rain <span class="gl-ds-unit">(RM)</span></label>
              <div class="gl-ds-input-prefix-wrap">
                <span class="gl-ds-prefix">RM</span>
                <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.rain_bonus" step="0.01" min="0">
              </div>
            </div>
            <div class="gl-ds-field">
              <label class="gl-ds-field-label">Late Night <span class="gl-ds-unit">(RM)</span></label>
              <div class="gl-ds-input-prefix-wrap">
                <span class="gl-ds-prefix">RM</span>
                <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="settings.late_night_bonus" step="0.01" min="0">
              </div>
            </div>
          </div>

          <div class="gl-ds-field-label" style="margin-bottom:10px">Peak Hours</div>
          <div class="gl-ds-peak-list">
            <div v-for="(period, index) in peakHours" :key="index" class="gl-ds-peak-row">
              <input type="time" class="gl-ds-input gl-ds-time-input" v-model="period.start">
              <span class="gl-ds-peak-to">to</span>
              <input type="time" class="gl-ds-input gl-ds-time-input" v-model="period.end">
              <button type="button" class="gl-ds-remove-btn" @click="removePeakHour(index)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div v-if="!peakHours.length" class="gl-ds-empty-hint">No peak hours defined</div>
          </div>
          <button type="button" class="gl-ds-add-btn" @click="addPeakHour">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Peak Hour Period
          </button>
        </div>

      </div>
    </div>

    <!-- API Credentials Row -->
    <div class="gl-ds-layout" style="margin-top:0">
      <div class="gl-ds-card">
        <div class="gl-ds-card-title">Lalamove API Credentials</div>
        <div class="gl-ds-field" style="margin-bottom:12px">
          <label class="gl-ds-field-label">API Key</label>
          <input type="text" class="gl-ds-input" v-model="lalamoveCredentials.api_key" placeholder="Enter Lalamove API Key">
        </div>
        <div class="gl-ds-field" style="margin-bottom:12px">
          <label class="gl-ds-field-label">API Secret</label>
          <input type="password" class="gl-ds-input" v-model="lalamoveCredentials.api_secret" placeholder="Enter Lalamove API Secret">
        </div>
        <div class="gl-ds-toggle-row" style="margin-bottom:14px">
          <div class="gl-ds-field-label">Sandbox Mode</div>
          <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': lalamoveCredentials.sandbox }" @click="lalamoveCredentials.sandbox = !lalamoveCredentials.sandbox">
            <span class="gl-toggle-thumb"></span>
          </button>
        </div>
        <button type="button" class="gl-btn-primary" @click="saveLalamoveCredentials" :disabled="savingLalamove">
          <span v-if="savingLalamove" class="gl-spin"></span>
          Save Lalamove Credentials
        </button>
      </div>

      <div class="gl-ds-card">
        <div class="gl-ds-card-title">Grab Express API Credentials</div>
        <div class="gl-ds-field" style="margin-bottom:12px">
          <label class="gl-ds-field-label">Client ID</label>
          <input type="text" class="gl-ds-input" v-model="grabCredentials.client_id" placeholder="Enter Grab Client ID">
        </div>
        <div class="gl-ds-field" style="margin-bottom:12px">
          <label class="gl-ds-field-label">Client Secret</label>
          <input type="password" class="gl-ds-input" v-model="grabCredentials.client_secret" placeholder="Enter Grab Client Secret">
        </div>
        <div class="gl-ds-toggle-row" style="margin-bottom:14px">
          <div class="gl-ds-field-label">Sandbox Mode</div>
          <button type="button" class="gl-toggle" :class="{ 'gl-toggle-on': grabCredentials.sandbox }" @click="grabCredentials.sandbox = !grabCredentials.sandbox">
            <span class="gl-toggle-thumb"></span>
          </button>
        </div>
        <button type="button" class="gl-btn-primary" @click="saveGrabCredentials" :disabled="savingGrab">
          <span v-if="savingGrab" class="gl-spin"></span>
          Save Grab Credentials
        </button>
      </div>
    </div>

    <!-- Fee Calculator -->
    <div class="gl-ds-card" style="margin-top:0">
      <div class="gl-ds-card-title">Fee Calculator Test</div>
      <div class="gl-ds-row-3" style="margin-bottom:16px">
        <div class="gl-ds-field">
          <label class="gl-ds-field-label">Distance <span class="gl-ds-unit">(km)</span></label>
          <input type="number" class="gl-ds-input" v-model.number="testFee.distance_km" step="0.1" min="0">
        </div>
        <div class="gl-ds-field">
          <label class="gl-ds-field-label">Order Total <span class="gl-ds-unit">(RM)</span></label>
          <div class="gl-ds-input-prefix-wrap">
            <span class="gl-ds-prefix">RM</span>
            <input type="number" class="gl-ds-input gl-ds-input-has-prefix" v-model.number="testFee.order_total" step="0.01" min="0">
          </div>
        </div>
        <div class="gl-ds-field">
          <label class="gl-ds-field-label">Postcode</label>
          <input type="text" class="gl-ds-input" v-model="testFee.postcode" placeholder="e.g. 50000">
        </div>
      </div>
      <button type="button" class="gl-btn-ghost" @click="calculateTestFee" style="margin-bottom:16px">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg>
        Calculate Fee
      </button>
      <div v-if="testResult" class="gl-calc-result">
        <div class="gl-calc-row"><span>Method</span><strong>{{ testResult.calculation_method }}</strong></div>
        <div class="gl-calc-row"><span>Delivery Fee</span><strong class="gl-calc-fee">RM {{ formatCurrency(testResult.delivery_fee) }}</strong></div>
        <div class="gl-calc-row"><span>Rider Commission</span><strong>RM {{ formatCurrency(testResult.rider_commission) }}</strong></div>
        <div class="gl-calc-row"><span>Current Bonus</span><strong>RM {{ formatCurrency(testResult.current_bonus) }}</strong></div>
        <div class="gl-calc-row"><span>Total Rider Earnings</span><strong>RM {{ formatCurrency(testResult.total_rider_earnings) }}</strong></div>
        <div class="gl-calc-row"><span>Within Range</span><strong>{{ testResult.is_within_range ? 'Yes' : 'No' }}</strong></div>
        <div v-if="testResult.is_free_delivery" class="gl-calc-free">🎉 Free Delivery!</div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminDeliveryApi } from '@/services/superAdminApi';

export default {
  name: 'DeliverySettings',
  data() {
    return {
      loading: false, saving: false, savingLalamove: false, savingGrab: false,
      settings: {
        inhouse_enabled: true, lalamove_enabled: false, grab_enabled: false,
        fee_calculation_method: 'fixed', fixed_delivery_fee: 5.00,
        base_fee: 3.00, base_distance_km: 3.00, per_km_fee: 1.50, max_delivery_fee: null,
        max_delivery_distance_km: 15.00, min_order_for_free_delivery: null,
        commission_type: 'fixed', commission_fixed_amount: 4.00, commission_percentage: 80.00,
        peak_hour_bonus: 0, rain_bonus: 0, late_night_bonus: 0,
        auto_assign_enabled: false, auto_assign_radius_km: 5, assignment_timeout_minutes: 5
      },
      meta: { fee_calculation_methods: {}, commission_types: {} },
      peakHours: [],
      lalamoveCredentials: { api_key: '', api_secret: '', sandbox: true },
      grabCredentials: { client_id: '', client_secret: '', sandbox: true },
      testFee: { distance_km: 5, order_total: 50, postcode: '' },
      testResult: null
    };
  },
  created() { this.fetchSettings(); },
  methods: {
    async fetchSettings() {
      this.loading = true;
      try {
        const r = await superAdminDeliveryApi.getSettings();
        if (r.data.success) {
          this.settings = { ...this.settings, ...r.data.data };
          this.meta = r.data.meta || this.meta;
          this.peakHours = this.settings.peak_hours || [];
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    async saveSettings() {
      this.saving = true;
      try {
        const data = { ...this.settings, peak_hours: this.peakHours.filter(p => p.start && p.end) };
        const r = await superAdminDeliveryApi.updateSettings(data);
        if (r.data.success) this.$toast?.success?.('Settings saved successfully');
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e.response?.data?.message || 'Failed to save settings');
      } finally { this.saving = false; }
    },
    async saveLalamoveCredentials() {
      this.savingLalamove = true;
      try {
        const r = await superAdminDeliveryApi.updateLalamove(this.lalamoveCredentials);
        if (r.data.success) { this.$toast?.success?.('Lalamove credentials saved'); this.lalamoveCredentials.api_key = ''; this.lalamoveCredentials.api_secret = ''; }
      } catch (e) { this.$toast?.error?.('Failed to save Lalamove credentials'); }
      finally { this.savingLalamove = false; }
    },
    async saveGrabCredentials() {
      this.savingGrab = true;
      try {
        const r = await superAdminDeliveryApi.updateGrab(this.grabCredentials);
        if (r.data.success) { this.$toast?.success?.('Grab credentials saved'); this.grabCredentials.client_id = ''; this.grabCredentials.client_secret = ''; }
      } catch (e) { this.$toast?.error?.('Failed to save Grab credentials'); }
      finally { this.savingGrab = false; }
    },
    async calculateTestFee() {
      try {
        const r = await superAdminDeliveryApi.testFeeCalculation(this.testFee);
        if (r.data.success) this.testResult = r.data.data;
        else this.$toast?.error?.(r.data.message);
      } catch (e) { this.$toast?.error?.('Failed to calculate fee'); }
    },
    addPeakHour() { this.peakHours.push({ start: '11:00', end: '14:00' }); },
    removePeakHour(i) { this.peakHours.splice(i, 1); },
    formatCurrency(v) { return v ? parseFloat(v).toFixed(2) : '0.00'; }
  }
};
</script>

<style>
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }
.gl-back-btn { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: #6b7f7b; text-decoration: none; margin-bottom: 8px; font-weight: 500; }
.gl-back-btn:hover { color: #900C27; }
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 10px 18px; font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: none; white-space: nowrap; }
.gl-btn-primary:hover { background: #1a5048; }
.gl-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.gl-btn-ghost { display: inline-flex; align-items: center; gap: 6px; background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 9px 16px; font-size: 13.5px; font-weight: 500; cursor: pointer; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }
.gl-btn-outline-inline { display: inline-flex; align-items: center; gap: 6px; padding: 9px 14px; border: 1.5px solid #e0e7e5; border-radius: 9px; color: #374151; font-size: 13px; font-weight: 500; text-decoration: none; }
.gl-btn-outline-inline:hover { border-color: #900C27; color: #900C27; }
.gl-ds-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.gl-ds-col { display: flex; flex-direction: column; gap: 16px; }
.gl-ds-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 22px 24px; }
.gl-ds-card-title { font-size: 12px; font-weight: 700; color: #6b7f7b; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 18px; }
.gl-ds-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.gl-ds-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
.gl-ds-field { display: flex; flex-direction: column; gap: 6px; }
.gl-ds-field-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-ds-field-hint { font-size: 11.5px; color: #9ca3af; }
.gl-ds-unit { font-weight: 400; color: #9ca3af; font-size: 11.5px; }
.gl-ds-input { width: 100%; padding: 9px 12px; border: 1.5px solid #e0e7e5; border-radius: 9px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; transition: border-color .15s; appearance: auto; }
.gl-ds-input:focus { border-color: #900C27; }
.gl-ds-input-prefix-wrap { position: relative; }
.gl-ds-input-suffix-wrap { position: relative; }
.gl-ds-prefix { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 12.5px; color: #9ca3af; font-weight: 600; pointer-events: none; }
.gl-ds-suffix { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); font-size: 12.5px; color: #9ca3af; font-weight: 600; pointer-events: none; }
.gl-ds-input-has-prefix { padding-left: 32px; }
.gl-ds-input-has-suffix { padding-right: 32px; }
.gl-ds-divider { height: 1px; background: #f0f3f2; margin: 16px 0; }
.gl-ds-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.gl-toggle { width: 44px; height: 24px; border-radius: 12px; background: #e0e7e5; border: none; cursor: pointer; position: relative; transition: background .2s; flex-shrink: 0; padding: 0; }
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; display: block; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.gl-toggle-on .gl-toggle-thumb { transform: translateX(20px); }
.gl-ds-provider-list { display: flex; flex-direction: column; gap: 0; }
.gl-ds-provider-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; border-bottom: 1px solid #f5f7f6; gap: 12px; }
.gl-ds-provider-row:last-child { border-bottom: none; padding-bottom: 0; }
.gl-ds-provider-row:first-child { padding-top: 0; }
.gl-ds-provider-info { display: flex; align-items: center; gap: 12px; }
.gl-ds-provider-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.gl-icon-inhouse { background: #e8f4f1; }
.gl-icon-lalamove { background: #fef9c3; }
.gl-icon-grab { background: #dcfce7; }
.gl-ds-provider-name { font-size: 13.5px; font-weight: 600; color: #111827; }
.gl-ds-provider-desc { font-size: 11.5px; color: #9ca3af; }
.gl-ds-peak-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }
.gl-ds-peak-row { display: flex; align-items: center; gap: 8px; }
.gl-ds-time-input { flex: 1; width: auto; }
.gl-ds-peak-to { font-size: 12px; color: #9ca3af; flex-shrink: 0; }
.gl-ds-remove-btn { width: 32px; height: 32px; flex-shrink: 0; border: 1.5px solid #e0e7e5; border-radius: 8px; background: #fff; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.gl-ds-remove-btn:hover { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
.gl-ds-empty-hint { font-size: 12.5px; color: #9ca3af; font-style: italic; }
.gl-ds-add-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1.5px dashed #c8d5d2; border-radius: 9px; background: transparent; color: #6b7f7b; font-size: 13px; font-weight: 500; cursor: pointer; width: 100%; justify-content: center; }
.gl-ds-add-btn:hover { border-color: #900C27; color: #900C27; background: #e8f4f1; }
.gl-calc-result { background: #f8faf9; border: 1px solid #eaedec; border-radius: 12px; padding: 16px 20px; display: flex; flex-direction: column; gap: 8px; }
.gl-calc-row { display: flex; justify-content: space-between; font-size: 13px; }
.gl-calc-row span { color: #6b7f7b; }
.gl-calc-fee { color: #16a34a; font-size: 15px; }
.gl-calc-free { text-align: center; font-weight: 700; color: #16a34a; font-size: 14px; padding-top: 4px; }
.gl-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }
@media (max-width: 900px) { .gl-ds-layout { grid-template-columns: 1fr; } .gl-ds-row-3 { grid-template-columns: 1fr 1fr; } }
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-page-header { flex-direction: column; }
  .gl-page-header .gl-btn-primary { width: 100%; justify-content: center; }
  .gl-ds-row-2 { grid-template-columns: 1fr; }
  .gl-ds-row-3 { grid-template-columns: 1fr; }
  .gl-ds-card { padding: 16px; }
}
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub,
body.dark-only .gl-back-btn { color: #94a3b8; }
body.dark-only .gl-back-btn:hover { color: #4ade80; }
body.dark-only .gl-ds-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-ds-card-title { color: #94a3b8; }
body.dark-only .gl-ds-field-label { color: #e2e8f0; }
body.dark-only .gl-ds-input { background: #162220; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-ds-input:focus { border-color: #900C27; }
body.dark-only .gl-toggle { background: #2d3f3b; }
body.dark-only .gl-ds-provider-row { border-color: #2d3f3b; }
body.dark-only .gl-ds-provider-name { color: #f1f5f9; }
body.dark-only .gl-ds-divider { background: #2d3f3b; }
body.dark-only .gl-ds-remove-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-calc-result { background: #162220; border-color: #2d3f3b; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-btn-outline-inline { border-color: #2d3f3b; color: #94a3b8; }
</style>
